import { useEffect, useMemo, useState } from "react";

import { getCampaignContacts } from "../services/campaignApi";

const RECIPIENT_MODES = [
  {
    value: "all",
    label: "Todos",
    description: "Todos los pacientes y familias con correo electrónico.",
    icon: "fa-users",
  },
  {
    value: "psychology",
    label: "Psicología",
    description: "Pacientes vinculados al área de psicología.",
    icon: "fa-brain",
  },
  {
    value: "logopedia",
    label: "Logopedia",
    description: "Pacientes vinculados al área de logopedia.",
    icon: "fa-comments",
  },
  {
    value: "nutrition",
    label: "Nutrición",
    description: "Pacientes vinculados al área de nutrición.",
    icon: "fa-apple-whole",
  },
  {
    value: "manual",
    label: "Seleccionar",
    description: "Elige individualmente los destinatarios.",
    icon: "fa-user-check",
  },
];

function getContactId(contact) {
  return (
    contact?.id ??
    contact?.patient_id ??
    contact?.patientId ??
    contact?.contact_id ??
    contact?.contactId ??
    null
  );
}

function getContactName(contact) {
  return (
    contact?.name ??
    contact?.nombre ??
    contact?.patient_name ??
    contact?.patientName ??
    contact?.full_name ??
    contact?.fullName ??
    "Sin nombre"
  );
}

function getContactEmail(contact) {
  return (
    contact?.email ??
    contact?.correo ??
    contact?.mail ??
    contact?.tutor_email ??
    contact?.tutorEmail ??
    ""
  );
}

function getContactPhone(contact) {
  return (
    contact?.phone ??
    contact?.telefono ??
    contact?.mobile ??
    contact?.movil ??
    contact?.tutor_phone ??
    contact?.tutorPhone ??
    ""
  );
}

function getContactSpecialty(contact) {
  return (
    contact?.specialty ??
    contact?.specialty_name ??
    contact?.specialtyName ??
    contact?.area ??
    contact?.especialidad ??
    ""
  );
}

function normalizeContactsResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.contacts)) {
    return response.contacts;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.patients)) {
    return response.patients;
  }

  return [];
}

function normalizeContact(contact) {
  return {
    ...contact,
    id: getContactId(contact),
    name: getContactName(contact),
    email: getContactEmail(contact),
    phone: getContactPhone(contact),
    specialty: getContactSpecialty(contact),
  };
}

function getSpecialtyCodes(recipientMode) {
  switch (recipientMode) {
    case "psychology":
      return ["psicologia"];

    case "logopedia":
      return ["logopedia"];

    case "nutrition":
      return ["nutricion"];

    default:
      return [];
  }
}

export default function DestinatariosCard({
  campaign,
  setCampaign,
}) {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState("");

  const recipientMode = campaign?.recipientMode || "all";

  const selectedRecipients = Array.isArray(campaign?.recipients)
    ? campaign.recipients
    : [];

  useEffect(() => {
    if (recipientMode !== "manual") {
      return undefined;
    }

    const controller = new AbortController();

    async function loadContacts() {
      try {
        setLoadingContacts(true);
        setContactsError("");

        const response = await getCampaignContacts({
          q: "",
          onlyWithEmail: true,
          signal: controller.signal,
        });

        const normalizedContacts = normalizeContactsResponse(response)
          .map(normalizeContact)
          .filter((contact) => contact.id !== null)
          .filter((contact) => Boolean(contact.email));

        setContacts(normalizedContacts);
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }

        console.error("Error cargando destinatarios:", error);

        setContactsError(
          error?.message ||
            "No se pudieron cargar los destinatarios.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoadingContacts(false);
        }
      }
    }

    loadContacts();

    return () => {
      controller.abort();
    };
  }, [recipientMode]);

  const filteredContacts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return contacts;
    }

    return contacts.filter((contact) => {
      const searchableText = [
        contact.name,
        contact.email,
        contact.phone,
        contact.specialty,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [contacts, search]);

  const selectedIds = useMemo(() => {
    return new Set(
      selectedRecipients
        .map((recipient) => getContactId(recipient))
        .filter((id) => id !== null)
        .map(String),
    );
  }, [selectedRecipients]);

  const selectedVisibleCount = useMemo(() => {
    return filteredContacts.filter((contact) =>
      selectedIds.has(String(contact.id)),
    ).length;
  }, [filteredContacts, selectedIds]);

  const allVisibleSelected =
    filteredContacts.length > 0 &&
    selectedVisibleCount === filteredContacts.length;

  function updateCampaign(updater) {
    setCampaign((currentCampaign) => {
      const safeCampaign = currentCampaign || {};

      return typeof updater === "function"
        ? updater(safeCampaign)
        : {
            ...safeCampaign,
            ...updater,
          };
    });
  }

  function handleModeChange(mode) {
    updateCampaign((currentCampaign) => ({
      ...currentCampaign,
      recipientMode: mode,
      recipients:
        mode === "manual"
          ? Array.isArray(currentCampaign.recipients)
            ? currentCampaign.recipients
            : []
          : [],
    }));

    setContactsError("");

    if (mode !== "manual") {
      setSearch("");
    }
  }

  function handleToggleRecipient(contact) {
    const contactId = String(contact.id);

    updateCampaign((currentCampaign) => {
      const currentRecipients = Array.isArray(
        currentCampaign.recipients,
      )
        ? currentCampaign.recipients
        : [];

      const alreadySelected = currentRecipients.some(
        (recipient) =>
          String(getContactId(recipient)) === contactId,
      );

      return {
        ...currentCampaign,
        recipients: alreadySelected
          ? currentRecipients.filter(
              (recipient) =>
                String(getContactId(recipient)) !== contactId,
            )
          : [
              ...currentRecipients,
              {
                id: contact.id,
                patient_id:
                  contact.patient_id ?? contact.id,
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                specialty: contact.specialty,
              },
            ],
      };
    });
  }

  function handleToggleAllVisible() {
    updateCampaign((currentCampaign) => {
      const currentRecipients = Array.isArray(
        currentCampaign.recipients,
      )
        ? currentCampaign.recipients
        : [];

      const visibleIds = new Set(
        filteredContacts.map((contact) =>
          String(contact.id),
        ),
      );

      if (allVisibleSelected) {
        return {
          ...currentCampaign,
          recipients: currentRecipients.filter(
            (recipient) =>
              !visibleIds.has(
                String(getContactId(recipient)),
              ),
          ),
        };
      }

      const existingIds = new Set(
        currentRecipients.map((recipient) =>
          String(getContactId(recipient)),
        ),
      );

      const recipientsToAdd = filteredContacts
        .filter(
          (contact) =>
            !existingIds.has(String(contact.id)),
        )
        .map((contact) => ({
          id: contact.id,
          patient_id:
            contact.patient_id ?? contact.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          specialty: contact.specialty,
        }));

      return {
        ...currentCampaign,
        recipients: [
          ...currentRecipients,
          ...recipientsToAdd,
        ],
      };
    });
  }

  function handleClearSelection() {
    updateCampaign((currentCampaign) => ({
      ...currentCampaign,
      recipients: [],
    }));
  }

  const selectedMode = RECIPIENT_MODES.find(
    (mode) => mode.value === recipientMode,
  );

  const automaticSpecialtyCodes =
    getSpecialtyCodes(recipientMode);

  return (
    <section className="campaign-card recipients-card">
      <div className="campaign-card-header">
        <div className="campaign-card-title">
          <span className="campaign-card-icon">
            <i className="fa-solid fa-users" />
          </span>

          <div>
            <h2>Destinatarios</h2>
            <p>
              Elige a qué pacientes o familias se enviará
              la campaña.
            </p>
          </div>
        </div>

        {recipientMode === "manual" && (
          <span className="campaign-count-badge">
            {selectedRecipients.length} seleccionados
          </span>
        )}
      </div>

      <div className="recipient-mode-selector">
        <span className="recipient-mode-label">Enviar a:</span>

        {RECIPIENT_MODES.map((mode) => {
          const isActive = recipientMode === mode.value;

          return (
            <button
              key={mode.value}
              type="button"
              className={`recipient-mode-button ${
                isActive ? "active" : ""
              }`}
              aria-pressed={isActive}
              onClick={() => handleModeChange(mode.value)}
            >
              <i
                className={`fa-solid ${mode.icon}`}
                aria-hidden="true"
              />
              <span>{mode.label}</span>
            </button>
          );
        })}
      </div>

      {recipientMode !== "manual" && (
        <div className="recipient-selection-summary compact">
          <i className="fa-solid fa-circle-info" />

          <div>
            <strong>
              {selectedMode?.label ||
                "Selección automática"}
            </strong>

            <p>
              {recipientMode === "all"
                ? "El servidor seleccionará automáticamente todos los contactos con correo electrónico."
                : `El servidor seleccionará automáticamente los contactos del área indicada${
                    automaticSpecialtyCodes.length
                      ? ` (${automaticSpecialtyCodes.join(
                          ", ",
                        )})`
                      : ""
                  }.`}
            </p>
          </div>
        </div>
      )}

      {recipientMode === "manual" && (
        <div className="manual-recipient-section">
          <div className="manual-recipient-toolbar">
            <label className="recipient-search">
              <i className="fa-solid fa-magnifying-glass" />

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Buscar por nombre, correo o teléfono..."
              />
            </label>

            <div className="manual-recipient-actions">
              <button
                type="button"
                className="recipient-secondary-button"
                onClick={handleToggleAllVisible}
                disabled={
                  loadingContacts ||
                  filteredContacts.length === 0
                }
              >
                <i
                  className={`fa-solid ${
                    allVisibleSelected
                      ? "fa-square-minus"
                      : "fa-square-check"
                  }`}
                />

                {allVisibleSelected
                  ? "Quitar visibles"
                  : "Seleccionar visibles"}
              </button>

              <button
                type="button"
                className="recipient-secondary-button"
                onClick={handleClearSelection}
                disabled={
                  selectedRecipients.length === 0
                }
              >
                <i className="fa-solid fa-eraser" />
                Limpiar
              </button>
            </div>
          </div>

          {loadingContacts && (
            <div className="recipient-state-message">
              <i className="fa-solid fa-spinner fa-spin" />
              Cargando destinatarios...
            </div>
          )}

          {!loadingContacts && contactsError && (
            <div className="recipient-state-message error">
              <i className="fa-solid fa-circle-exclamation" />
              {contactsError}
            </div>
          )}

          {!loadingContacts &&
            !contactsError &&
            contacts.length === 0 && (
              <div className="recipient-state-message">
                <i className="fa-solid fa-inbox" />
                No hay contactos disponibles con correo
                electrónico.
              </div>
            )}

          {!loadingContacts &&
            !contactsError &&
            contacts.length > 0 &&
            filteredContacts.length === 0 && (
              <div className="recipient-state-message">
                <i className="fa-solid fa-magnifying-glass" />
                No se encontraron resultados para la
                búsqueda.
              </div>
            )}

          {!loadingContacts &&
            !contactsError &&
            filteredContacts.length > 0 && (
              <div className="recipient-list">
                {filteredContacts.map((contact) => {
                  const isSelected = selectedIds.has(
                    String(contact.id),
                  );

                  return (
                    <button
                      key={contact.id}
                      type="button"
                      className={`recipient-list-item ${
                        isSelected ? "selected" : ""
                      }`}
                      onClick={() =>
                        handleToggleRecipient(contact)
                      }
                    >
                      <span
                        className={`recipient-checkbox ${
                          isSelected ? "checked" : ""
                        }`}
                      >
                        {isSelected && (
                          <i className="fa-solid fa-check" />
                        )}
                      </span>

                      <span className="recipient-avatar">
                        {contact.name
                          .trim()
                          .charAt(0)
                          .toUpperCase() || "?"}
                      </span>

                      <span className="recipient-information">
                        <strong>{contact.name}</strong>

                        <span className="recipient-email">
                          <i className="fa-solid fa-envelope" />
                          {contact.email}
                        </span>

                        {(contact.phone ||
                          contact.specialty) && (
                          <small>
                            {contact.specialty && (
                              <span>
                                <i className="fa-solid fa-tag" />
                                {contact.specialty}
                              </span>
                            )}

                            {contact.phone && (
                              <span>
                                <i className="fa-solid fa-phone" />
                                {contact.phone}
                              </span>
                            )}
                          </small>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

          <div className="manual-recipient-footer">
            <span>
              <strong>
                {selectedRecipients.length}
              </strong>{" "}
              destinatarios seleccionados
            </span>

            {search.trim() && (
              <span>
                {filteredContacts.length} resultados
                visibles
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}